import React, { useMemo } from 'react';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import EditIcon from '@mui/icons-material/Edit';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Alert, Box, CircularProgress } from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  type GridColDef,
  type GridRowSelectionModel,
} from '@mui/x-data-grid';
import type { StrapiManagedUserRow } from '../api/strapiUsers';

export type UsersGridRowActions = {
  resolveApiId: (row: StrapiManagedUserRow) => string | null;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onAssignPreset: (id: string) => void;
  onImpersonate?: (id: string) => void;
  /** When true, disables impersonate action (e.g. already impersonating). */
  impersonateDisabled?: boolean;
};

function formatDate(s?: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

function formatUserTypeDisplay(code: string | null, name: string | null): string {
  const c = code ?? '';
  const n = name ?? '';
  if (c && n) return `${c} — ${n}`;
  return c || n;
}

type UsersDataGridProps = {
  rows: StrapiManagedUserRow[];
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  rowActions?: UsersGridRowActions | null;
  checkboxSelection?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (selection: GridRowSelectionModel) => void;
};

export function UsersDataGrid({
  rows,
  isLoading,
  isError,
  error,
  rowActions,
  checkboxSelection = false,
  rowSelectionModel,
  onRowSelectionModelChange,
}: UsersDataGridProps) {
  const columns: GridColDef<StrapiManagedUserRow>[] = useMemo(
    () => {
      const base: GridColDef<StrapiManagedUserRow>[] = [
        { field: 'username', headerName: 'Username', minWidth: 140, flex: 0.6 },
        { field: 'email', headerName: 'Email', minWidth: 220, flex: 1 },
        {
          field: 'userTypeCode',
          headerName: 'User type',
          minWidth: 180,
          flex: 0.55,
          valueGetter: (_v, row) => formatUserTypeDisplay(row.userTypeCode, row.userTypeName),
        },
        {
          field: 'blocked',
          headerName: 'Status',
          minWidth: 100,
          valueFormatter: (value: unknown) =>
            typeof value === 'boolean' ? (value ? 'Inactive' : 'Active') : '',
        },
        {
          field: 'confirmed',
          headerName: 'Confirmed',
          minWidth: 100,
          valueFormatter: (value: unknown) =>
            value === undefined || value === null ? '' : value ? 'Yes' : 'No',
        },
        {
          field: 'createdAt',
          headerName: 'Created',
          minWidth: 160,
          valueFormatter: (v) => (typeof v === 'string' ? formatDate(v) : ''),
        },
      ];

      if (rowActions) {
        base.push({
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: rowActions.onImpersonate ? 170 : 130,
          getActions: (params) => {
            const apiId = rowActions.resolveApiId(params.row);
            if (!apiId) return [];
            const items = [
              <GridActionsCellItem
                key="view"
                icon={<VisibilityIcon />}
                label="View"
                onClick={() => rowActions.onView(apiId)}
              />,
            ];
            if (rowActions.onImpersonate) {
              items.push(
                <GridActionsCellItem
                  key="impersonate"
                  icon={<SwitchAccountIcon />}
                  label="Impersonate"
                  onClick={() => rowActions.onImpersonate!(apiId)}
                  disabled={rowActions.impersonateDisabled || params.row.blocked}
                />,
              );
            }
            items.push(
              <GridActionsCellItem
                key="edit"
                icon={<EditIcon />}
                label="Edit"
                onClick={() => rowActions.onEdit(apiId)}
                showInMenu
              />,
              <GridActionsCellItem
                key="preset"
                icon={<AssignmentIndIcon />}
                label="Assign permission preset"
                onClick={() => rowActions.onAssignPreset(apiId)}
                showInMenu
              />,
            );
            return items;
          },
        });
      }

      return base;
    },
    [rowActions],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (isError) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Failed to load users.'}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        isRowSelectable={(params) => params.row.numericId != null}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
        autoHeight
        density="compact"
        sx={{ '& .MuiDataGrid-columnHeaders': { fontWeight: 600 } }}
      />
    </Box>
  );
}
